import {
    useCustomersPreviewFilter,
    FilterValues,
    CustomerPreview,
} from '@/hooks/useCustomersPreviewFilter'
import { Button, FormItem, Typogaphy } from 'vnetwork-ui'
import { useForm, FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Filter, Search, Loader2 } from 'lucide-react'
import { useMemo, useState, useCallback, useEffect } from 'react'
import EmployeeSelect from '@/components/Form/Shared/EmployeeSelect'
import { INDUSTRIES, CUSTOMER_STATUS } from '@/modules/Customer/constants'
import {
    CUSTOMER_STATUS_OPTIONS,
    REQUIRED_FIELD_OPTIONS,
    SERVICE_STATUS_OPTIONS,
} from '@/modules/MailerCampaign/types'
import CustomerPreviewTable from '@/components/CustomerPreviewTable'

import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getProductsGroupOptions } from '@/modules/ProductGroup/services'
import { DEPARTMENT_IDS } from '@/constants/departments'

export type CustomerSelectionValues = {
    mode: 'filter' | 'manual'
    filters?: Partial<FilterValues>
    selectedIds?: number[]
    selectedCustomers?: CustomerPreview[]
}

interface CustomerPreviewSectionProps {
    onSelectionChanged?: (selection: CustomerSelectionValues) => void
    defaultMode?: 'filter' | 'manual'
    showModeTabs?: boolean
    defaultSelection?: CustomerSelectionValues
    requiredCustomerIds?: number[]
}

interface CustomerPreviewSectionFormValues {
    data_source_type: string
    customer_status: string[]
    customer_industry: string[]
    customer_sale_followers: number[]
    customer_cs_followers: number[]
    required_fields: string[]
    product_group_name: string[]
    service_status: string[]
    service_interestings: string[]
}

// Status groups for conditional logic
const SERVICE_RELATED_STATUSES = [
    CUSTOMER_STATUS.ACTIVE,
    CUSTOMER_STATUS.TRIAL,
    CUSTOMER_STATUS.STOP,
]
const POTENTIAL_CUSTOMER_STATUS = CUSTOMER_STATUS.COOLLEAD

// What "in use" means everywhere else in the system: db/queries/customer.active.sql
// gates its services EXISTS on service_status IN ('active', 'waiting-create-service').
// 'waiting-create-service' is an approved service still waiting on Technical to switch
// it on, so it counts as in use.
const IN_USE_SERVICE_STATUSES = ['active', 'waiting-create-service']

const CustomerPreviewSection = ({
    onSelectionChanged,
    defaultMode = 'filter',
    showModeTabs = false,
    defaultSelection,
    requiredCustomerIds,
}: CustomerPreviewSectionProps) => {
    const { t } = useTranslation('surveys')
    // The service-status.* labels live in the `services` namespace, not in
    // `surveys`, and fallbackNS ('translation') only has `service-status` as a
    // plain string -- so resolving them through `t` renders the raw i18n keys.
    const { t: tServices } = useTranslation('services')
    const {
        customersPreview = {
            results: [],
            pagination: {
                total: 0,
                page: 1,
                page_size: 10,
                total_pages: 0,
                page_next: 0,
                page_prev: 0,
            },
        },
        isLoading,
        fetchCustomersPreview,
        currentPageSize,
        currentSearch,
    } = useCustomersPreviewFilter()
    const [mode, setMode] = useState<'filter' | 'manual'>(
        defaultSelection?.mode || defaultMode
    )
    const [excludedCustomerIds, setExcludedCustomerIds] = useState<number[]>(
        defaultSelection?.filters?.exclude_customer_ids || []
    )
    const [selectedManualIds, setSelectedManualIds] = useState<number[]>(
        defaultSelection?.selectedIds || []
    )
    const [selectedCustomers, setSelectedCustomers] = useState<
        CustomerPreview[]
    >(defaultSelection?.selectedCustomers || [])

    // Customers already surveyed this quarter. Hidden by default so the list only
    // shows work left to do; the toggle brings them back, flagged and unselectable.
    const surveyedCustomerIds = useMemo(
        () => requiredCustomerIds ?? [],
        [requiredCustomerIds]
    )
    const [showSurveyed, setShowSurveyed] = useState(false)

    // Needed before the form is built: the service_status default below is
    // derived from it.
    const initialCustomerStatus = defaultSelection?.filters
        ?.customer_status || ['active']

    // Default the service-status filter to "in use". The "DV dang su dung" field
    // filters on product_group_name, but the backend only constrains status when
    // service_status is non-empty -- that predicate lives in the LEFT JOIN's ON
    // clause (reports.customers.sql), so an empty service_status lets a cancelled
    // service match the product group and the customer shows up as still using it.
    // Skip the default when the user is looking for customers who have stopped,
    // otherwise their (cancelled) services would all be filtered away.
    const savedServiceStatus = defaultSelection?.filters?.service_status
    const initialServiceStatus =
        savedServiceStatus && savedServiceStatus.length > 0
            ? savedServiceStatus
            : initialCustomerStatus.includes(CUSTOMER_STATUS.STOP)
              ? []
              : IN_USE_SERVICE_STATUSES

    const form = useForm<CustomerPreviewSectionFormValues>({
        defaultValues: {
            data_source_type: 'customers',
            customer_status: initialCustomerStatus,
            customer_industry:
                defaultSelection?.filters?.customer_industry || [],
            customer_sale_followers:
                defaultSelection?.filters?.customer_sale_followers || [],
            customer_cs_followers:
                defaultSelection?.filters?.customer_cs_followers || [],
            required_fields: defaultSelection?.filters?.required_fields || [],
            product_group_name:
                defaultSelection?.filters?.product_group_name || [],
            service_status: initialServiceStatus,
            service_interestings:
                defaultSelection?.filters?.service_interestings || [],
        },
    })

    const { watch, handleSubmit, setValue } = form
    const formValues = watch()

    // ----------------------------------------------------
    // Conditional Visibility Logic (Ported from InternalFiltersSection)
    // ----------------------------------------------------
    const customerStatus = formValues.customer_status || []

    const visibility = useMemo(() => {
        // Count how many of active/trial/stop are selected
        const serviceRelatedCount = customerStatus.filter((status) =>
            SERVICE_RELATED_STATUSES.includes(status)
        ).length

        // Check if coollead (potential customer) is selected
        const hasPotentialCustomer = customerStatus.includes(
            POTENTIAL_CUSTOMER_STATUS
        )

        return {
            // Show product_group_name if any of active/trial/stop is selected
            showProductGroup: serviceRelatedCount > 0,
            // Show service_status whenever product_group_name shows: the two are
            // a pair. product_group_name on its own matches a service in ANY
            // status, so while this was hidden "DV dang su dung" could not
            // actually mean "dang su dung".
            showServiceStatus: serviceRelatedCount > 0,
            // Show service_interestings if coollead is selected
            showServiceInterestings: hasPotentialCustomer,
        }
    }, [customerStatus])

    // Clear fields when conditions are no longer met
    useEffect(() => {
        if (
            !visibility.showProductGroup &&
            formValues.product_group_name?.length
        ) {
            setValue('product_group_name', [])
        }
    }, [
        visibility.showProductGroup,
        formValues.product_group_name?.length,
        setValue,
    ])

    useEffect(() => {
        if (
            !visibility.showServiceStatus &&
            formValues.service_status?.length
        ) {
            setValue('service_status', [])
        }
    }, [
        visibility.showServiceStatus,
        formValues.service_status?.length,
        setValue,
    ])

    useEffect(() => {
        if (
            !visibility.showServiceInterestings &&
            formValues.service_interestings?.length
        ) {
            setValue('service_interestings', [])
        }
    }, [
        visibility.showServiceInterestings,
        formValues.service_interestings?.length,
        setValue,
    ])

    // Fetch product groups from API
    const {
        data: productGroupOptions = [],
        isLoading: isLoadingProductGroups,
    } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.PRODUCT_GROUPS, 'options'],
        queryFn: () => getProductsGroupOptions(),
    })

    // ----------------------------------------------------
    // Options
    // ----------------------------------------------------
    const industryOptions = INDUSTRIES.map((industry) => ({
        value: industry.value,
        label: industry.label,
    }))

    const customerStatusOptions = CUSTOMER_STATUS_OPTIONS(t).map((opt) => ({
        value: opt.value,
        label: opt.label,
    }))

    const requiredFieldOptions = REQUIRED_FIELD_OPTIONS.map((opt) => ({
        value: opt.value,
        label: t(opt.labelKey),
    }))

    const serviceStatusOptions = SERVICE_STATUS_OPTIONS(tServices).map(
        (opt) => ({
            value: opt.value,
            label: opt.label,
        })
    )

    // Check if any filters are set
    const hasFilters = useMemo(() => {
        return (
            (formValues.customer_status?.length ?? 0) > 0 ||
            (formValues.customer_industry?.length ?? 0) > 0 ||
            (formValues.customer_sale_followers?.length ?? 0) > 0 ||
            (formValues.required_fields?.length ?? 0) > 0 ||
            (formValues.product_group_name?.length ?? 0) > 0 ||
            (formValues.service_status?.length ?? 0) > 0 ||
            (formValues.service_interestings?.length ?? 0) > 0
        )
    }, [formValues])

    const onSubmit = (data: CustomerPreviewSectionFormValues) => {
        setExcludedCustomerIds([]) // Reset exclusions on new search
        // We don't reset manual selection on search, typically you want to keep them while searching more.

        const filters: Partial<FilterValues> = {
            customer_status: data.customer_status || [],
            customer_industry: data.customer_industry || [],
            customer_sale_followers: data.customer_sale_followers || [],
            customer_cs_followers: data.customer_cs_followers || [],
            required_fields: data.required_fields || [],
            product_group_name: data.product_group_name || [],
            service_status: data.service_status || [],
            service_interestings: data.service_interestings || [],
            exclude_customer_ids: [
                ...(showSurveyed ? [] : surveyedCustomerIds),
                ...excludedCustomerIds,
            ],
        }

        // Notify parent of current state (filters updated)
        onSelectionChanged?.({
            mode,
            filters,
            selectedIds: selectedManualIds,
            selectedCustomers,
        })

        fetchCustomersPreview(
            {
                filters,
                page: 1, // Reset to first page on new filter
            },
            true
        )
    }

    useEffect(() => {
        const data = form.getValues()
        const filters: Partial<FilterValues> = {
            customer_status: data.customer_status || [],
            customer_industry: data.customer_industry || [],
            customer_sale_followers: data.customer_sale_followers || [],
            customer_cs_followers: data.customer_cs_followers || [],
            required_fields: data.required_fields || [],
            product_group_name: data.product_group_name || [],
            service_status: data.service_status || [],
            service_interestings: data.service_interestings || [],
            exclude_customer_ids: showSurveyed ? [] : surveyedCustomerIds,
        }
        fetchCustomersPreview({ filters, page: 1 }, true)
    }, [])

    const handleToggleShowSurveyed = (next: boolean) => {
        setShowSurveyed(next)
        const data = form.getValues()
        const filters: Partial<FilterValues> = {
            customer_status: data.customer_status || [],
            customer_industry: data.customer_industry || [],
            customer_sale_followers: data.customer_sale_followers || [],
            customer_cs_followers: data.customer_cs_followers || [],
            required_fields: data.required_fields || [],
            product_group_name: data.product_group_name || [],
            service_status: data.service_status || [],
            service_interestings: data.service_interestings || [],
            exclude_customer_ids: [
                ...(next ? [] : surveyedCustomerIds),
                ...excludedCustomerIds,
            ],
        }
        fetchCustomersPreview({ filters, page: 1 }, true)
    }

    const handlePageChange = useCallback(
        (page: number) => {
            fetchCustomersPreview({
                page,
                page_size: currentPageSize,
                search: currentSearch, // Preserve current search
            })
        },
        [fetchCustomersPreview, currentPageSize, currentSearch]
    )

    const handleSearch = useCallback(
        (search: string) => {
            fetchCustomersPreview({
                search,
                page: 1, // Reset to first page on search
            })
        },
        [fetchCustomersPreview]
    )

    const handleToggleExcludeCustomer = (customerId: number) => {
        setExcludedCustomerIds((prev) => {
            const newExcluded = prev.includes(customerId)
                ? prev.filter((id) => id !== customerId)
                : [...prev, customerId]

            // Sync with parent immediately if in filter mode
            if (mode === 'filter') {
                const currentFilters = form.getValues()
                const filters: Partial<FilterValues> = {
                    customer_status: currentFilters.customer_status || [],
                    customer_industry: currentFilters.customer_industry || [],
                    customer_sale_followers:
                        currentFilters.customer_sale_followers || [],
                    customer_cs_followers:
                        currentFilters.customer_cs_followers || [],
                    required_fields: currentFilters.required_fields || [],
                    product_group_name: currentFilters.product_group_name || [],
                    service_status: currentFilters.service_status || [],
                    service_interestings:
                        currentFilters.service_interestings || [],
                    exclude_customer_ids: newExcluded,
                }

                onSelectionChanged?.({
                    mode: 'filter',
                    filters,
                    selectedIds: [],
                    selectedCustomers: [],
                })
            }

            return newExcluded
        })
    }

    const handleToggleSelectCustomer = (customerId: number) => {
        setSelectedManualIds((prevIds) => {
            const isSelected = prevIds.includes(customerId)
            const newSelectedIds = isSelected
                ? prevIds.filter((id) => id !== customerId)
                : [...prevIds, customerId]

            // Update selected customer objects
            let newSelectedCustomers = [...selectedCustomers]
            if (isSelected) {
                newSelectedCustomers = newSelectedCustomers.filter(
                    (c) => c.customer_id !== customerId
                )
            } else {
                const customer = customersPreview.results.find(
                    (c) => c.customer_id === customerId
                )
                if (customer) {
                    newSelectedCustomers.push(customer)
                }
            }
            setSelectedCustomers(newSelectedCustomers)

            // Sync with parent immediately
            if (mode === 'manual') {
                // If in manual mode, filters don't matter as much, but we still pass current form values
                const currentFilters = form.getValues()
                const filters: Partial<FilterValues> = {
                    customer_status: currentFilters.customer_status || [],
                    customer_industry: currentFilters.customer_industry || [],
                    customer_sale_followers:
                        currentFilters.customer_sale_followers || [],
                    customer_cs_followers:
                        currentFilters.customer_cs_followers || [],
                    required_fields: currentFilters.required_fields || [],
                    product_group_name: currentFilters.product_group_name || [],
                    service_status: currentFilters.service_status || [],
                    service_interestings:
                        currentFilters.service_interestings || [],
                    exclude_customer_ids: [],
                }

                onSelectionChanged?.({
                    mode: 'manual',
                    filters,
                    selectedIds: newSelectedIds,
                    selectedCustomers: newSelectedCustomers,
                })
            }

            return newSelectedIds
        })
    }

    const handleToggleSelectAll = (
        customerIds: number[],
        isSelected: boolean
    ) => {
        setSelectedManualIds((prevIds) => {
            let newSelectedIds = [...prevIds]
            let newSelectedCustomers = [...selectedCustomers]

            if (isSelected) {
                // Add ids that are not already selected
                customerIds.forEach((id) => {
                    if (!newSelectedIds.includes(id)) {
                        newSelectedIds.push(id)
                        const customer = customersPreview.results.find(
                            (c) => c.customer_id === id
                        )
                        if (customer) {
                            newSelectedCustomers.push(customer)
                        }
                    }
                })
            } else {
                // Remove ids
                newSelectedIds = newSelectedIds.filter(
                    (id) => !customerIds.includes(id)
                )
                newSelectedCustomers = newSelectedCustomers.filter(
                    (c) => !customerIds.includes(c.customer_id)
                )
            }

            setSelectedCustomers(newSelectedCustomers)

            if (mode === 'manual') {
                const currentFilters = form.getValues()
                const filters: Partial<FilterValues> = {
                    customer_status: currentFilters.customer_status || [],
                    customer_industry: currentFilters.customer_industry || [],
                    customer_sale_followers:
                        currentFilters.customer_sale_followers || [],
                    customer_cs_followers:
                        currentFilters.customer_cs_followers || [],
                    required_fields: currentFilters.required_fields || [],
                    product_group_name: currentFilters.product_group_name || [],
                    service_status: currentFilters.service_status || [],
                    service_interestings:
                        currentFilters.service_interestings || [],
                    exclude_customer_ids: [],
                }

                onSelectionChanged?.({
                    mode: 'manual',
                    filters,
                    selectedIds: newSelectedIds,
                    selectedCustomers: newSelectedCustomers,
                })
            }

            return newSelectedIds
        })
    }

    const handleModeChange = (newMode: string) => {
        const m = newMode as 'filter' | 'manual'
        setMode(m)
        const currentFilters = form.getValues()
        const filters: Partial<FilterValues> = {
            customer_status: currentFilters.customer_status || [],
            customer_industry: currentFilters.customer_industry || [],
            customer_sale_followers:
                currentFilters.customer_sale_followers || [],
            customer_cs_followers: currentFilters.customer_cs_followers || [],
            required_fields: currentFilters.required_fields || [],
            product_group_name: currentFilters.product_group_name || [],
            service_status: currentFilters.service_status || [],
            service_interestings: currentFilters.service_interestings || [],
            exclude_customer_ids: m === 'filter' ? excludedCustomerIds : [],
        }

        onSelectionChanged?.({
            mode: m,
            filters,
            selectedIds: m === 'manual' ? selectedManualIds : [],
            selectedCustomers: m === 'manual' ? selectedCustomers : [],
        })
    }

    // Available variables for customers
    const availableVariables = [
        { name: 'email', label: 'Email' },
        { name: 'name', label: 'Tên khách hàng' },
        { name: 'company', label: 'Tên công ty' },
        { name: 'phone', label: 'Số điện thoại' },
    ]

    return (
        <FormProvider {...form}>
            <div className="rounded-lg">
                <div data-tour="survey-customer-filters">
                    <div className="flex flex-col gap-2 p-4 bg-white">
                        <div className="flex items-center gap-2 ">
                            <Filter size={14} className="text-blue-600" />
                            <Typogaphy className="text-sm font-medium text-blue-700">
                                {t('filter_customers')}
                            </Typogaphy>
                        </div>
                        {showModeTabs && (
                            <Tabs
                                value={mode}
                                onValueChange={handleModeChange}
                                className="w-full mb-4 bg-white"
                            >
                                <TabsList className="grid w-full grid-cols-2">
                                    <TabsTrigger value="filter">
                                        {t('mode_filter', 'Use Filters')}
                                    </TabsTrigger>
                                    <TabsTrigger value="manual">
                                        {t('mode_manual', 'Select Manually')}
                                    </TabsTrigger>
                                </TabsList>
                            </Tabs>
                        )}
                        {/* Filters Grid - 2 columns */}
                        <div className="grid grid-cols-2 gap-3 bg-white">
                            {/* Data Source Type - Read-only */}
                            <FormItem
                                control={form.control}
                                name="data_source_type"
                                type="select"
                                label={t('data_source_type')}
                                placeholder={t('data_source_type')}
                                options={[
                                    {
                                        value: 'customers',
                                        label: t('customers') || 'Khách Hàng',
                                    },
                                ]}
                                disabled
                                allowClear={false}
                                size="small"
                            />
                            {/* Customer Status */}
                            <FormItem
                                control={form.control}
                                name="customer_status"
                                type="select"
                                label={t('customer_status')}
                                placeholder={t('select_customer_status')}
                                options={customerStatusOptions}
                                multiple
                                size="small"
                                defaultValue={['active']}
                                disabled
                            />
                            {/* Customer Industry */}
                            <FormItem
                                control={form.control}
                                name="customer_industry"
                                type="select"
                                label={t('customer_industry')}
                                placeholder={t('select_customer_industry')}
                                options={industryOptions}
                                multiple
                                size="small"
                            />
                            {/* Product Group - Only show if active/trial/stop selected */}
                            {visibility.showProductGroup && (
                                <FormItem
                                    control={form.control}
                                    name="product_group_name"
                                    type="select"
                                    label={t('product_group')}
                                    placeholder={t('select_product_group')}
                                    options={productGroupOptions}
                                    multiple
                                    loading={isLoadingProductGroups}
                                    size="small"
                                />
                            )}
                            {/* Service Status - Only show if 2+ of active/trial/stop selected */}
                            {visibility.showServiceStatus && (
                                <FormItem
                                    control={form.control}
                                    name="service_status"
                                    type="select"
                                    label={t('service_status')}
                                    placeholder={t('select_service_status')}
                                    options={serviceStatusOptions}
                                    multiple
                                    size="small"
                                />
                            )}
                            {/* Service Interestings - Only show if coollead (potential customer) selected */}
                            {visibility.showServiceInterestings && (
                                <FormItem
                                    control={form.control}
                                    name="service_interestings"
                                    type="select"
                                    label={t('service_interestings')}
                                    placeholder={t(
                                        'select_service_interestings'
                                    )}
                                    options={productGroupOptions}
                                    multiple
                                    loading={isLoadingProductGroups}
                                    size="small"
                                />
                            )}
                            {/* Sale Followers - Full width */}
                            <div>
                                <EmployeeSelect
                                    control={form.control}
                                    name="customer_sale_followers"
                                    multiple
                                    label="sale_followers"
                                    departmentIds={[DEPARTMENT_IDS.SALES]}
                                />
                            </div>
                            {/* CS Followers - Full width */}
                            <div>
                                <EmployeeSelect
                                    control={form.control}
                                    name="customer_cs_followers"
                                    multiple
                                    label="cs_followers"
                                    departmentIds={[DEPARTMENT_IDS.ACCOUNT]}
                                />
                            </div>
                            {/* Required Fields - Full width */}
                            <div className="col-span-2">
                                <FormItem
                                    control={form.control}
                                    name="required_fields"
                                    type="select"
                                    label={t('required_fields')}
                                    placeholder={t('select_required_fields')}
                                    options={requiredFieldOptions}
                                    multiple
                                    size="small"
                                    helperText={t('required_fields_helper')}
                                />
                            </div>
                        </div>
                        {/* Preview Button & Variables - Inline */}
                        <div className="flex items-start justify-between gap-4 mt-3 pt-3 border-t border-blue-200">
                            <Button
                                type="button"
                                variant="outlined"
                                size="small"
                                onClick={handleSubmit(onSubmit)}
                                disabled={
                                    !hasFilters ||
                                    isLoading ||
                                    requiredCustomerIds === undefined
                                }
                                startIcon={
                                    isLoading ? (
                                        <Loader2
                                            size={14}
                                            className="animate-spin"
                                        />
                                    ) : (
                                        <Search size={14} />
                                    )
                                }
                            >
                                {t('preview_results')}
                            </Button>
                            {/* Available Variables */}
                            <div className="flex-1">
                                <Typogaphy className="text-xs font-medium text-green-700 mb-1">
                                    {t('available_variables')}:
                                </Typogaphy>
                                <div className="flex flex-wrap gap-1">
                                    {availableVariables.map(
                                        (variable, index) => (
                                            <code
                                                key={variable.name}
                                                className={`px-1.5 py-0.5 border rounded text-[10px] font-mono ${
                                                    index === 0
                                                        ? 'bg-blue-100 border-blue-300 text-blue-800'
                                                        : 'bg-green-100 border-green-300 text-green-800'
                                                }`}
                                                title={variable.label}
                                            >
                                                {`{{${variable.name}}}`}
                                            </code>
                                        )
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Customer Preview Table */}
                <div data-tour="survey-customer-preview">
                    <div className="bg-white p-4">
                        {surveyedCustomerIds.length > 0 && (
                            <label className="mb-2 flex w-fit cursor-pointer items-center gap-2 text-xs text-gray-600">
                                <input
                                    type="checkbox"
                                    className="h-3.5 w-3.5 cursor-pointer accent-blue-600"
                                    checked={showSurveyed}
                                    onChange={(e) =>
                                        handleToggleShowSurveyed(
                                            e.target.checked
                                        )
                                    }
                                />
                                {t('surveys.filter.show_surveyed', {
                                    count: surveyedCustomerIds.length,
                                })}
                            </label>
                        )}
                        <CustomerPreviewTable
                            customers={customersPreview.results || []}
                            pagination={customersPreview.pagination}
                            isLoading={isLoading}
                            excludedCustomerIds={excludedCustomerIds}
                            onToggleExcludeCustomer={
                                handleToggleExcludeCustomer
                            }
                            surveyedCustomerIds={
                                showSurveyed ? surveyedCustomerIds : []
                            }
                            surveyedLabel={t('surveys.filter.surveyed_badge')}
                            selectedCustomerIds={selectedManualIds}
                            onToggleSelectCustomer={handleToggleSelectCustomer}
                            onToggleSelectAll={handleToggleSelectAll}
                            selectionMode={
                                mode === 'manual' ? 'include' : 'exclude'
                            }
                            onPageChange={handlePageChange}
                            onSearch={handleSearch}
                            showExcludeToggle={mode === 'filter'}
                            showSearch={true}
                        />
                    </div>
                </div>
            </div>
        </FormProvider>
    )
}

export default CustomerPreviewSection
